/**
 * @fileoverview Pure field-list reducer extracted from PdfFieldEditor.tsx
 * (MOR-774). No React, no PDF.js — every rectangle-list mutation the editor
 * performs (place, patch, remove, split into cell groups, bulk checkbox
 * default/group/ungroup, apply LLM key suggestions) goes through this
 * reducer, so the state machine is testable without mounting the canvas.
 */
import { FIELD_CATEGORIES } from "@cvx/settings/precedents/canonicalFields";
import { parseContactFieldKey } from "@cvx/jobs/precedents/contactFields";
import type { DateCalc } from "@cvx/settings/precedents/dateCalc";
import type { CustomFieldDef } from "./CanonicalKeyPicker";

export interface FieldPosition {
  id: string;
  canonicalKey: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize?: number;
  fieldType?: string;
  /**
   * Number of character cells this rectangle spans. When set, the fill
   * action draws each character of the value into its own evenly-spaced
   * cell across `width` (used for ABN/ACN/TFN/date box rows). One
   * rectangle per visible group; multi-group fields (e.g. ABN's 2/3/3/3)
   * are placed as multiple rectangles all sharing the same canonicalKey.
   */
  charBoxes?: number;
  /**
   * Offset into the source value for the first cell in this group. E.g.
   * ABN group three (digits 6-8) sets charStart = 5.
   */
  charStart?: number;
  /**
   * Legacy yes/no pattern: which state of the linked boolean field this
   * box represents — "checked" (default) or "unchecked". Superseded by
   * `groupKey` + `optionValue` for new templates, kept for back-compat.
   */
  checkboxValue?: string;
  /**
   * For checkbox placements: default state at job time. A box with a
   * default and no canonical/group binding is "standalone" — it is
   * hidden from the field-review form and the user toggles it
   * inline on the rendered PDF.
   */
  defaultChecked?: boolean;
  /**
   * Group multiple checkbox placements into a single-select question.
   * All boxes that share the same `groupKey` collectively represent one
   * value (e.g. "Application type" → Personal / Concession / Home office).
   * At fill time, the box whose `optionValue` matches the current value
   * of the group key is ticked.
   */
  groupKey?: string;
  /** Human-readable label for the group (set on any one member). */
  groupLabel?: string;
  /** This box's option value/label within the group. */
  optionValue?: string;
  /**
   * Dynamic date calculation for date placements — derives this field's
   * value from another date field when no explicit value is provided
   * (e.g. Records Period From = 10 years before `appointment_date`).
   * Resolved at fill time; see convex/settings/precedents/dateCalc.ts.
   */
  dateCalc?: DateCalc;
  /**
   * The printed PDF section this field sits under. Not edited here, but
   * kept on the type so loaded positions round-trip through autosave
   * without dropping the key.
   */
  section?: string;
}

/**
 * Default cell counts when a known boxed canonical key is first picked.
 * Only applied when the field's `charBoxes` is currently undefined, so
 * we never overwrite a user-tweaked value.
 */
export const DEFAULT_CHAR_BOXES: Record<string, number> = {
  abn: 11,
  firm_abn: 11,
  acn: 9,
  tfn: 9,
  postcode: 4,
  firm_postcode: 4,
  // Dates print as DD/MM/YYYY — 8 digit cells, split 2/2/4.
  signature_date: 8,
};

export function makeFieldId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Parse a user-entered split pattern like "3,3,3" or "2 3 3 3" into an
 * array of positive group sizes. Returns null if the pattern is empty or
 * contains any non-positive / non-integer entries (the Split button is
 * disabled in that case so the user gets immediate feedback).
 */
export function parseSplitPattern(input: string): number[] | null {
  const parts = input
    .split(/[,\s/+]+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (parts.length === 0) return null;
  const groups: number[] = [];
  for (const p of parts) {
    const n = Number(p);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) return null;
    groups.push(n);
  }
  return groups;
}

/**
 * Build a field patch that applies a canonical key plus the side-effects
 * normally handled by the key-change handler: inferring the field type from
 * the canonical/custom definition and seeding `charBoxes` / `charStart`
 * defaults for known boxed fields (ABN/ACN/TFN/postcode). Shared between the
 * user-driven picker and the LLM auto-suggest paths so both produce the same
 * Cells + Groups split UI without the user having to re-key the field.
 */
export function buildKeyChangePatch(
  existing: FieldPosition,
  key: string,
  customFields: CustomFieldDef[],
): Partial<FieldPosition> {
  const canonical = FIELD_CATEGORIES.flatMap((c) => c.fields).find(
    (f) => f.key === key,
  );
  const custom = customFields.find((f) => f.key === key);
  // A contact date of birth (`contact_<domain>_date_of_birth`) is a date too
  // (MOR-2342 BKY-08), so its boxed groups slice digits, not slashes.
  const contactDate =
    parseContactFieldKey(key)?.part === "date_of_birth" ? "date" : undefined;
  const inferredType = canonical?.fieldType ?? custom?.fieldType ?? contactDate;
  const defaultBoxes = DEFAULT_CHAR_BOXES[key];

  const patch: Partial<FieldPosition> = {
    canonicalKey: key,
    ...(inferredType ? { fieldType: inferredType } : {}),
  };
  if (defaultBoxes !== undefined && existing.charBoxes === undefined) {
    patch.charBoxes = defaultBoxes;
  }
  if (
    existing.charStart === undefined &&
    (patch.charBoxes !== undefined || existing.charBoxes !== undefined)
  ) {
    patch.charStart = 0;
  }
  return patch;
}

export type FieldsAction =
  | { type: "replaceAll"; fields: FieldPosition[] }
  | { type: "add"; field: FieldPosition }
  | { type: "patch"; id: string; patch: Partial<FieldPosition> }
  | { type: "remove"; id: string }
  /** Apply LLM-suggested canonical keys to every field that doesn't already
   *  have one (auto-detect and the manual "Suggest again" re-run). */
  | {
      type: "applySuggestedKeys";
      keys: ReadonlyMap<string, string>;
      customFields: CustomFieldDef[];
    }
  /** Sets (or clears, when dateCalc is undefined) a date calc across every
   *  placement of a canonical key — a date split into day/month/year boxes
   *  is several rectangles sharing one key, all of which need the calc so
   *  every slice fills. Falls back to a single field when there's no key. */
  | {
      type: "setDateCalc";
      id: string;
      canonicalKey: string;
      dateCalc: DateCalc | undefined;
    }
  /** Replaces one rectangle with `groups.length` sibling rectangles, sized
   *  in proportion to their cell counts and laid out left-to-right from the
   *  original position. `newIds` must have the same length as `groups` —
   *  id generation is impure (random), so the caller supplies them. */
  | { type: "split"; id: string; groups: number[]; newIds: string[] }
  | { type: "bulkSetDefault"; ids: ReadonlySet<string>; checked: boolean }
  | { type: "bulkClearDefault"; ids: ReadonlySet<string> }
  | {
      type: "applyGrouping";
      ids: ReadonlySet<string>;
      groupKey: string;
      groupLabel: string;
      optionsById: Record<string, string>;
    }
  | { type: "ungroup"; ids: ReadonlySet<string> };

export function fieldsReducer(
  state: FieldPosition[],
  action: FieldsAction,
): FieldPosition[] {
  switch (action.type) {
    case "replaceAll":
      return action.fields;

    case "add":
      return [...state, action.field];

    case "patch":
      return state.map((f) =>
        f.id === action.id ? { ...f, ...action.patch } : f,
      );

    case "remove":
      return state.filter((f) => f.id !== action.id);

    case "applySuggestedKeys":
      return state.map((f) => {
        const k = action.keys.get(f.id);
        if (!k || f.canonicalKey) return f;
        return { ...f, ...buildKeyChangePatch(f, k, action.customFields) };
      });

    case "setDateCalc":
      return state.map((f) =>
        (action.canonicalKey
          ? f.canonicalKey === action.canonicalKey
          : f.id === action.id) && f.fieldType === "date"
          ? { ...f, dateCalc: action.dateCalc }
          : f,
      );

    case "split": {
      const { id, groups, newIds } = action;
      if (newIds.length !== groups.length) return state;
      const idx = state.findIndex((f) => f.id === id);
      if (idx === -1) return state;
      const src = state[idx];
      const totalCells = groups.reduce((a, b) => a + b, 0);
      if (totalCells === 0) return state;
      const cellWidth = src.width / totalCells;
      const replacements: FieldPosition[] = groups.map((g, i) => {
        const offsetCells = groups.slice(0, i).reduce((a, b) => a + b, 0);
        const width = g * cellWidth;
        const x = src.x + offsetCells * cellWidth;
        return {
          ...src,
          id: newIds[i],
          x,
          width,
          charBoxes: g,
          charStart: offsetCells,
        };
      });
      const next = [...state];
      next.splice(idx, 1, ...replacements);
      return next;
    }

    case "bulkSetDefault":
      return state.map((f) =>
        action.ids.has(f.id) && f.fieldType === "checkbox"
          ? { ...f, defaultChecked: action.checked }
          : f,
      );

    case "bulkClearDefault":
      return state.map((f) => {
        if (!action.ids.has(f.id) || f.fieldType !== "checkbox") return f;
        const { defaultChecked: _drop, ...rest } = f;
        return rest;
      });

    case "applyGrouping":
      return state.map((f) => {
        if (!action.ids.has(f.id) || f.fieldType !== "checkbox") return f;
        return {
          ...f,
          groupKey: action.groupKey,
          groupLabel: action.groupLabel,
          // Clear legacy yes/no pattern so the renderer goes through the
          // new group path. Canonical link is also cleared — the group
          // itself is the canonical question now.
          checkboxValue: undefined,
          canonicalKey: "",
          optionValue:
            (action.optionsById[f.id] ?? "").trim() || (f.optionValue ?? ""),
        };
      });

    case "ungroup":
      return state.map((f) => {
        if (!action.ids.has(f.id) || f.fieldType !== "checkbox") return f;
        const { groupKey: _gk, groupLabel: _gl, optionValue: _ov, ...rest } = f;
        return rest;
      });

    default:
      return state;
  }
}
